// Links for the panel's `{ type, slug }` contact / social entries. Pure, no imports (tested with `node --test`).
//
// The old table built wrong or non-profile urls: linkedin `https://linkedin/in/<slug>` (no domain), x on twitter.com,
// bale with no domain (`https://<slug>`), youtube / aparat as a *video* link (`youtu.be/`, `aparat.com/v/`), pinterest
// as a short pin link (`pin.it/`), an unknown type as `https://undefined<slug>`, and any slug (spaces, `?`, a second
// full url) was pasted in as is. Reported by chawk-bazar / ciseco, which kept their own copies for the author page and
// the home page `sameAs`.

const media_types = [
  { title: "usersTable.mobile", value: "mobile", prefix: "tel:" },
  { title: "usersTable.email", value: "email", prefix: "mailto:" },
  { title: "social_medias.telegram", value: "telegram", prefix: "t.me/", hosts: ["t.me", "telegram.me"], profile: true },
  { title: "social_medias.whatsapp", value: "whatsapp", prefix: "wa.me/", hosts: ["wa.me", "api.whatsapp.com", "whatsapp.com"] },
  { title: "social_medias.x", value: "x", prefix: "x.com/", hosts: ["x.com", "twitter.com"], profile: true },
  { title: "social_medias.eitaa", value: "eitaa", prefix: "eitaa.com/", hosts: ["eitaa.com"], profile: true },
  { title: "social_medias.instagram", value: "instagram", prefix: "www.instagram.com/", hosts: ["instagram.com"], profile: true },
  { title: "social_medias.linkedin", value: "linkedin", prefix: "www.linkedin.com/in/", hosts: ["linkedin.com"], profile: true },
  { title: "social_medias.pinterest", value: "pinterest", prefix: "www.pinterest.com/", hosts: ["pinterest.com", "pin.it"], profile: true },
  { title: "social_medias.soroosh", value: "soroosh", prefix: "splus.ir/", hosts: ["splus.ir"], profile: true },
  { title: "social_medias.bale", value: "bale", prefix: "ble.ir/", hosts: ["ble.ir"], profile: true },
  { title: "social_medias.rubika", value: "rubika", prefix: "rubika.ir/", hosts: ["rubika.ir"], profile: true },
  { title: "social_medias.youtube", value: "youtube", prefix: "www.youtube.com/", hosts: ["youtube.com", "youtu.be"], profile: true },
  { title: "social_medias.aparat", value: "aparat", prefix: "www.aparat.com/", hosts: ["aparat.com"], profile: true },
];

// a user name or a short path on the network (`name`, `company/name`, `channel/UC..`, `joinchat/AbC`, `+AbC`)
const SAFE_SLUG = /^[\w.\-+~@%]+(?:\/[\w.\-+~@%]+){0,2}\/?$/;

const find_type = (type) => media_types.find((item) => item.value === (typeof type === "string" ? type.trim().toLowerCase() : type));

// A full url in the slug is kept only when it is https and on that network's own domain.
const network_url = (media, raw) => {
  try {
    const url = new URL(raw.replace(/^http:\/\//i, "https://"));
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    if (url.username || url.password || !media.hosts?.includes(host) || url.pathname.length < 2) return "";
    return `https://${url.hostname.toLowerCase()}${url.pathname}${url.search}`;
  } catch (e) {
    return "";
  }
};

const network_path = (media, raw) => {
  let slug = raw.replace(/^\/+/, "");
  if (media.value === "youtube") {
    // a bare name is a handle (`@name`); `@name`, `channel/…`, `c/…` and `user/…` are kept
    if (!slug.includes("/") && !slug.startsWith("@")) slug = `@${slug}`;
  } else if (media.value === "linkedin") {
    // `company/name` and `in/name` are paths of their own
    if (/^(in|company|school|showcase)\//i.test(slug)) return SAFE_SLUG.test(slug) ? `https://www.linkedin.com/${slug}` : "";
    slug = slug.replace(/^@/, "");
  } else {
    slug = slug.replace(/^@/, "");
  }
  return SAFE_SLUG.test(slug) ? `https://${media.prefix}${slug}` : "";
};

// wa.me wants the international number without `+` or leading zeros (an Iranian 09.. becomes 989..)
const whatsapp_number = (raw) => {
  let digits = raw.replace(/[\s()-]/g, "").replace(/^\+/, "");
  if (!/^\d{6,15}$/.test(digits)) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = `98${digits.slice(1)}`;
  return digits;
};

/**
 * Link for a panel entry: `tel:` / `mailto:` for mobile and email, a chat link for whatsapp, otherwise the https
 * address on the network. An unknown type or a slug that cannot be a link gives "" (never `https://undefined…`), so
 * a caller can skip it with a truthiness check.
 */
export const social_media_path = (type, slug) => {
  const media = find_type(type);
  const raw = typeof slug === "string" ? slug.trim() : typeof slug === "number" ? String(slug) : "";
  if (!media || !raw) return "";

  // a panel value may already carry its scheme (isource: `mailto:a@b.ir` gave `mailto:mailto:…`)
  if (media.value === "mobile") {
    const phone = raw.replace(/^tel:/i, "").replace(/[\s()-]/g, "");
    return /^\+?\d{3,15}$/.test(phone) ? `tel:${phone}` : "";
  }
  if (media.value === "email") {
    const email = raw.replace(/^mailto:/i, "").trim();
    return /^[^\s@/?#:]+@[^\s@/?#:]+\.[^\s@/?#:]+$/.test(email) ? `mailto:${email}` : "";
  }
  if (/^https?:\/\//i.test(raw)) return network_url(media, raw);
  if (media.value === "whatsapp") {
    const number = whatsapp_number(raw);
    return number ? `https://wa.me/${number}` : "";
  }
  return network_path(media, raw);
};

/**
 * Public profile address of a social network entry, for an author page or JSON-LD `sameAs`; null for a type that is
 * not a profile (mobile, email, whatsapp), an unknown type, or a slug that cannot be a link.
 */
export const social_profile_url = (type, slug) => {
  const media = find_type(type);
  if (!media?.profile) return null;
  return social_media_path(media.value, slug) || null;
};
